/**
 * External certificates on the Records tab (plan docs/plans/lms-plan.md 7.5,
 * 5.5 lms_external_certs; R17; WP-41): the certificates the person uploaded,
 * newest first, each with its review state (pending until an admin accepts
 * or rejects it; a rejected upload stays visible with the reviewer's note),
 * the requirement it was offered against, and View (GET
 * /external-certs/:id/url, a presigned URL for 10 minutes, opened in a new
 * tab the same way a certificate is: the tab opens on the click, 18.27).
 * "Upload certificate" opens ExternalCertForm.
 */
import { useState } from 'react';
import { Eye, FileBadge, Upload } from 'lucide-react';
import EmptyState from '../../../ui/EmptyState';
import { outlineBtnCls, primaryBtnCls } from '../../../ui/styles';
import { useToast } from '../../../ui/Toast';
import { apiErrorMessage } from '../../api/client';
import { getExternalCertUrl, listMyExternalCerts } from '../../api/externalCerts';
import type { ExternalCert, ExternalCertStatus } from '../../api/externalCerts';
import type { LmsList } from '../../api/client';
import { formatDay } from '../../components/DueBadge';
import { openPendingTab } from '../../components/player/DoneStage';
import { Pill } from '../../components/StatusPill';
import type { PillTone } from '../../components/StatusPill';
import { Section, SectionError, SectionSkeleton } from '../home/Section';
import { useLoad } from '../home/useLoad';
import { ExternalCertForm } from './ExternalCertForm';

/** 5.5 lms_external_certs.status with its word and color (13: never color alone). */
export const EXTERNAL_STATUS_PILL: Readonly<Record<ExternalCertStatus, { tone: PillTone; word: string }>> = {
  pending: { tone: 'amber', word: 'Pending review' },
  accepted: { tone: 'green', word: 'Accepted' },
  rejected: { tone: 'red', word: 'Rejected' },
};

/** Rows per page (Appendix A: at most 200); one person uploads a handful. */
export const EXTERNAL_PAGE = 200;

const thCls = 'px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400';
const tdCls = 'px-4 py-3 text-sm text-gray-900 dark:text-gray-100';

export function ExternalCertsSection({ reloadKey }: { reloadKey: number }) {
  const { toast } = useToast();
  const certs = useLoad<LmsList<ExternalCert>>(
    () => listMyExternalCerts({ limit: EXTERNAL_PAGE }),
    'Your external certificates could not be loaded.',
    reloadKey
  );
  const [formOpen, setFormOpen] = useState(false);
  const [opening, setOpening] = useState<string | null>(null);
  const [blocked, setBlocked] = useState<{ id: string; url: string } | null>(null);
  const rows = certs.data?.items ?? [];

  const view = async (row: ExternalCert) => {
    if (opening) return;
    // Before any await: opened inside the click's user activation (18.27).
    const tab = openPendingTab();
    setOpening(row.id);
    setBlocked(null);
    try {
      const { url } = await getExternalCertUrl(row.id, 'inline');
      if (tab && !tab.closed) tab.location.replace(url);
      else setBlocked({ id: row.id, url });
    } catch (err) {
      if (tab && !tab.closed) tab.close();
      toast(apiErrorMessage(err, 'The file could not be opened. Try again.'), { type: 'error' });
    } finally {
      setOpening(null);
    }
  };

  return (
    <Section
      title="External certificates"
      icon={FileBadge}
      count={certs.data ? rows.length : null}
      headingId="lms-records-external-title"
      data-testid="lms-external-certs"
      actions={
        <button type="button" className={primaryBtnCls} onClick={() => setFormOpen(true)} data-testid="lms-extcert-open">
          <Upload className="h-4 w-4" aria-hidden="true" />
          Upload certificate
        </button>
      }
    >
      {certs.loading ? (
        <SectionSkeleton rows={2} />
      ) : certs.error ? (
        <SectionError message={certs.error} onRetry={certs.reload} data-testid="lms-external-error" />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={FileBadge}
          title="No external certificates"
          hint="Upload a certificate you earned elsewhere, such as CITI GCP or CPR, so an admin can count it toward a requirement."
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-testid="lms-external-table">
            <thead className="bg-gray-50 dark:bg-gray-800/50">
              <tr>
                <th scope="col" className={thCls}>Certificate</th>
                <th scope="col" className={thCls}>Issued</th>
                <th scope="col" className={thCls}>Expires</th>
                <th scope="col" className={thCls}>Counts toward</th>
                <th scope="col" className={thCls}>Status</th>
                <th scope="col" className={thCls}>File</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {rows.map((r) => {
                const pill = EXTERNAL_STATUS_PILL[r.status] ?? { tone: 'gray' as PillTone, word: r.status };
                return (
                  <tr key={r.id} className="align-top" data-testid="lms-external-row" data-status={r.status}>
                    <td className={tdCls}>
                      <div className="font-medium">{r.title}</div>
                      <div className="text-xs text-gray-600 dark:text-gray-400">
                        {r.credential_id ? `${r.issuer} \u00b7 ${r.credential_id}` : r.issuer}
                      </div>
                    </td>
                    <td className={`${tdCls} whitespace-nowrap`}>{formatDay(r.issued_on)}</td>
                    <td className={`${tdCls} whitespace-nowrap`}>{r.expires_on ? formatDay(r.expires_on) : 'Does not expire'}</td>
                    <td className={tdCls}>{r.course?.title ?? <span className="text-gray-500 dark:text-gray-400">Not linked</span>}</td>
                    <td className={tdCls}>
                      <Pill tone={pill.tone} data-testid="lms-external-status">
                        {pill.word}
                      </Pill>
                      {r.status !== 'pending' && r.reviewed_at ? (
                        <div className="mt-1 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">{`Reviewed ${formatDay(r.reviewed_at)}`}</div>
                      ) : null}
                      {r.review_note ? (
                        <div className="mt-1 max-w-xs text-xs text-gray-700 dark:text-gray-300" data-testid="lms-external-note">
                          {`Note: ${r.review_note}`}
                        </div>
                      ) : null}
                    </td>
                    <td className={tdCls}>
                      <button
                        type="button"
                        className={outlineBtnCls()}
                        onClick={() => void view(r)}
                        disabled={opening === r.id}
                        aria-label={`View ${r.title}`}
                        data-testid="lms-external-view"
                      >
                        <Eye className="h-4 w-4" aria-hidden="true" />
                        View
                      </button>
                      {blocked && blocked.id === r.id ? (
                        <a
                          href={blocked.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-1 block text-xs font-medium text-primary-600 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-400"
                          data-testid="lms-external-link"
                        >
                          Open the file (the link works for 10 minutes)
                        </a>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <ExternalCertForm isOpen={formOpen} onClose={() => setFormOpen(false)} onUploaded={() => certs.reload()} />
    </Section>
  );
}

export default ExternalCertsSection;
